/* A mentor's own diary. */

/* Same shape as the sessions tabs, so the portal reads as one product. */
.tabs {
  display: flex;
  gap: var(--space-1);
  padding: var(--space-1);
  background: var(--bg-sunken);
  border: 1px solid var(--border);
  border-radius: var(--radius-md);
  width: fit-content;
}

.tab {
  padding: var(--space-2) var(--space-4);
  font: inherit;
  font-size: var(--text-sm);
  font-weight: 500;
  color: var(--text-muted);
  background: none;
  border: 0;
  border-radius: var(--radius-sm);
  cursor: pointer;
}

.tab:hover {
  color: var(--text);
}

.tabOn {
  color: var(--text);
  background: var(--bg-elevated);
  box-shadow: var(--shadow-sm);
}

.sectionTitle {
  margin: 0 0 var(--space-1);
  font-size: var(--text-md);
  font-weight: 600;
  color: var(--text);
}

.sectionNote {
  margin: 0 0 var(--space-4);
  font-size: var(--text-sm);
  line-height: var(--leading-normal);
  color: var(--text-muted);
}

/* ------------------------------------------------------- weekly hours */

.days {
  display: flex;
  flex-direction: column;
}

.day {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: var(--space-3);
  padding: var(--space-3) 0;
  border-bottom: 1px solid var(--border);
}

.day:last-child {
  border-bottom: 0;
}

/*
  A day the INSTITUTE is closed. Dimmed rather than removed: the week
  should keep its shape, and a missing Sunday reads as a bug.
*/
.dayClosed {
  opacity: 0.55;
}

.dayToggle {
  display: flex;
  align-items: center;
  gap: var(--space-3);
  cursor: pointer;
}

.dayClosed .dayToggle {
  cursor: default;
}

.dayName {
  font-size: var(--text-base);
  font-weight: 500;
  color: var(--text);
}

.dayNote {
  font-size: var(--text-sm);
  color: var(--text-muted);
}

.times {
  display: flex;
  align-items: center;
  gap: var(--space-2);
}

.time {
  padding: var(--space-2) var(--space-3);
  font: inherit;
  font-size: var(--text-sm);
  font-variant-numeric: tabular-nums;
  color: var(--text);
  background: var(--bg-elevated);
  border: 1px solid var(--border);
  border-radius: var(--radius);
}

.toLabel {
  font-size: var(--text-sm);
  color: var(--text-muted);
}

/* ------------------------------------------------------------- breaks */

.breakList {
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
}

.breakRow {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: var(--space-2);
}

.select {
  padding: var(--space-2) var(--space-3);
  font: inherit;
  font-size: var(--text-sm);
  color: var(--text);
  background: var(--bg-elevated);
  border: 1px solid var(--border);
  border-radius: var(--radius);
}

.labelInput {
  flex: 1 1 8rem;
  min-width: 0;
  padding: var(--space-2) var(--space-3);
  font: inherit;
  font-size: var(--text-sm);
  color: var(--text);
  background: var(--bg-elevated);
  border: 1px solid var(--border);
  border-radius: var(--radius);
}

.time:focus,
.select:focus,
.labelInput:focus {
  outline: 2px solid var(--indigo);
  outline-offset: -1px;
}

.removeX {
  flex-shrink: 0;
  width: 1.75rem;
  height: 1.75rem;
  font: inherit;
  font-size: var(--text-md);
  line-height: 1;
  color: var(--text-muted);
  background: none;
  border: 1px solid var(--border);
  border-radius: var(--radius);
  cursor: pointer;
}

.removeX:hover {
  color: var(--danger);
  border-color: var(--danger);
}

.oneOffList {
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
  margin: var(--space-4) 0 0;
  padding: 0;
  list-style: none;
}

.oneOffRow {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-3);
  padding: var(--space-3);
  font-size: var(--text-sm);
  background: var(--bg-sunken);
  border-radius: var(--radius);
}

/* ------------------------------------------------------------ time off */

.offRow {
  display: flex;
  align-items: flex-end;
  flex-wrap: wrap;
  gap: var(--space-3);
}

.offField {
  display: flex;
  flex-direction: column;
  gap: var(--space-1);
}

.fieldLabel {
  font-size: var(--text-xs);
  font-weight: 600;
  color: var(--text-muted);
}

/* -------------------------------------------------------- the dialog */

.backdrop {
  position: fixed;
  inset: 0;
  z-index: var(--z-modal);
  display: grid;
  place-items: center;
  padding: var(--space-4);
  background: rgb(15 23 42 / 45%);
}

.dialog {
  display: flex;
  flex-direction: column;
  gap: var(--space-4);
  width: min(32rem, 100%);
  max-height: 85dvh;
  overflow-y: auto;
  padding: var(--space-5);
  background: var(--bg-elevated);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-lg);
}

.dialogTitle {
  margin: 0;
  font-size: var(--text-lg);
  font-weight: 600;
  color: var(--text);
}

.dialogText {
  margin: 0;
  font-size: var(--text-sm);
  line-height: var(--leading-normal);
  color: var(--text);
}

.clashes {
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
  margin: 0;
  padding: 0;
  list-style: none;
}

.clash {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: var(--space-3);
  padding: var(--space-3);
  font-size: var(--text-sm);
  background: var(--warning-soft);
  border-radius: var(--radius);
}

.clashWhen {
  font-weight: 500;
  color: var(--text);
}

.clashWho {
  color: var(--text-muted);
}

.dialogActions {
  display: flex;
  justify-content: space-between;
  gap: var(--space-3);
}

/* ---------------------------------------------------------------- misc */

.actions {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: var(--space-3);
  margin-top: var(--space-5);
}

/* Something true that the mentor should weigh, not an error. */
.warnNote {
  font-size: var(--text-sm);
  color: var(--warning);
}

.muted {
  margin: 0;
  color: var(--text-muted);
  font-size: var(--text-sm);
}

.error {
  margin: 0;
  color: var(--danger);
  font-size: var(--text-sm);
}

@media (max-width: 40rem) {
  /* The seven-day list wraps hard otherwise. */
  .day {
    align-items: flex-start;
    flex-direction: column;
  }

  .tabs {
    width: 100%;
  }

  .tab {
    flex: 1;
  }
}
